import { useState } from "react";

import { useLive } from "../lib/use-live";
import type { ReportSummary } from "../types/api";
import { useT } from "../context/locale";
import { money } from "../lib/format";
import { Button, ErrorNote, Panel, StatTile } from "../components/ui";
import { DateField, SelectField } from "../components/ui/form";

export function Reports() {
  const t = useT();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [excludeTest, setExcludeTest] = useState("true");

  const report = useLive<ReportSummary>("report", {
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
    excludeTest,
  });

  const data = report.data;
  const profitable = (data?.grossProfitCents ?? 0) >= 0;

  return (
    <div className="space-y-5">
      <Panel eyebrow={t("nav.group.money")} title={t("reports.title")}>
        <div className="grid items-end gap-4 sm:grid-cols-4">
          <DateField label={t("common.from")} value={from} onChange={setFrom} />
          <DateField label={t("common.to")} value={to} onChange={setTo} />
          <SelectField
            label={t("reports.testAccounts")}
            value={excludeTest}
            onChange={setExcludeTest}
            options={[
              { value: "true", label: t("reports.excluded") },
              { value: "false", label: t("reports.included") },
            ]}
          />
          <Button
            onClick={() => {
              setFrom("");
              setTo("");
            }}
          >
            {t("reports.clearDates")}
          </Button>
        </div>
      </Panel>

      {report.error ? (
        <ErrorNote message={report.error} onRetry={report.reload} />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label={t("dashboard.turnover")}
          value={data ? money(data.turnoverCents) : "—"}
          hint={t("reports.turnoverHint")}
          tone="gold"
        />
        <StatTile
          label={t("dashboard.paidOut")}
          value={data ? money(data.payoutCents) : "—"}
          hint={t("draws.returnedToWinners")}
          tone="info"
        />
        <StatTile
          label={t("dashboard.grossProfit")}
          value={data ? money(data.grossProfitCents) : "—"}
          hint={
            data
              ? t("reports.marginOfTurnover", { value: data.marginPercent })
              : undefined
          }
          tone={profitable ? "success" : "danger"}
        />
        <StatTile
          label={t("reports.roundsSettled")}
          value={data ? data.drawCount.toLocaleString() : "—"}
          hint={data ? `${data.betCount.toLocaleString()} bets` : undefined}
          tone="plain"
        />
      </div>

      <Panel eyebrow={t("reports.reading")} title={t("reports.meaning")}>
        <div className="text-muted space-y-3 text-[13px]">
          <p>
            <strong className="text-body">{t("dashboard.turnover")}</strong>{" "}
            {t("reports.turnoverExplain")}
          </p>
          <p>
            <strong className="text-body">{t("dashboard.grossProfit")}</strong>{" "}
            {t("reports.profitExplain")}
          </p>
          <p>
            <strong className="text-body">{t("reports.testAccounts")}</strong>{" "}
            {t("reports.testExplain")}
          </p>
        </div>
      </Panel>
    </div>
  );
}
